import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { useReducedMotion } from "../lib/motion";

/**
 * Beyond Sure — Background Orbs (v3.4).
 *
 * Adds three to four soft, slowly-drifting glow orbs in brand green on top of
 * the canvas. They sit BEHIND HexAmbient so they read as ambient depth
 * (think "stadium lights through fog"), not as foreground confetti.
 *
 * Performance:
 *   • GPU-only: every animated property is transform/opacity. No layout
 *     reads, no paint cost beyond the initial composite.
 *   • Hardware accelerated via will-change + translate3d (force layer).
 *   • Long durations (12-22s) so the CPU spends most of the frame idle.
 *
 * Phone parallax:
 *   • The component listens to window scroll and translates each orb at a
 *     small fraction of the scroll delta — a true parallax effect that
 *     proves the background is alive (not a static image). The desktop
 *     LEFT panel barely scrolls (the split is min-h-[100dvh]) so this is
 *     primarily a phone/tablet enhancement.
 *
 * Reduced motion:
 *   • If the user prefers reduced motion, render the orbs at their resting
 *     position with opacity dialed back. No GSAP timeline, no scroll
 *     listener. Static-but-elegant fallback.
 *
 * Props:
 *   tone:    "dark" (sits on canvas-green) | "light" (sits on mint)
 *   intensity: 0..1 overall opacity multiplier (default 1)
 */

interface Props {
  tone?: "dark" | "light";
  intensity?: number;
}

const ORBS = [
  // x/y in % of the parent container; size in vmax to scale with viewport.
  { id: "a", x: 12, y: 28, sizeVmax: 38, color: "rgb(31 216 118 / 0.18)", driftX: 12, driftY: -8, dur: 14 },
  { id: "b", x: 78, y: 18, sizeVmax: 30, color: "rgb(62 142 91 / 0.22)",  driftX: -10, driftY: 12, dur: 18 },
  { id: "c", x: 30, y: 78, sizeVmax: 44, color: "rgb(31 216 118 / 0.14)", driftX: 14, driftY: -10, dur: 22 },
  { id: "d", x: 88, y: 70, sizeVmax: 26, color: "rgb(62 142 91 / 0.20)",  driftX: -8, driftY: -6, dur: 16 },
];

export function BgOrbs({ tone = "dark", intensity = 1 }: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const reduced = useReducedMotion();

  // GSAP drift timelines — one per orb, looping yoyo.
  useEffect(() => {
    if (reduced) return;
    const container = containerRef.current;
    if (!container) return;

    const ctx = gsap.context(() => {
      ORBS.forEach((orb) => {
        const el = container.querySelector(`[data-orb="${orb.id}"]`) as HTMLElement | null;
        if (!el) return;
        gsap.to(el, {
          x: `${orb.driftX}%`,
          y: `${orb.driftY}%`,
          duration: orb.dur,
          ease: "sine.inOut",
          repeat: -1,
          yoyo: true,
        });
      });
    }, container);

    return () => ctx.revert();
  }, [reduced]);

  // Scroll parallax — translate each orb at a fraction of the scroll delta.
  // Only attaches when motion is permitted; uses passive listener for perf.
  useEffect(() => {
    if (reduced) return;
    let raf = 0;
    let lastY = window.scrollY;

    const applyParallax = () => {
      const container = containerRef.current;
      if (!container) return;
      const y = window.scrollY;
      lastY = y;
      ORBS.forEach((orb, i) => {
        const el = container.querySelector(`[data-orb="${orb.id}"]`) as HTMLElement | null;
        if (!el) return;
        // Different fractions per orb so layers separate visually.
        const frac = 0.04 + i * 0.025;
        el.style.setProperty("--orb-scroll-y", `${y * frac}px`);
      });
    };

    const onScroll = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        applyParallax();
        raf = 0;
      });
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    applyParallax();
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (raf) window.cancelAnimationFrame(raf);
      void lastY;
    };
  }, [reduced]);

  return (
    <div
      ref={containerRef}
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 overflow-hidden"
      style={{ opacity: tone === "light" ? 0.6 * intensity : intensity }}
    >
      {/* Animated gradient sheen — a slow, large-scale color wash across the
          canvas. Uses background-position keyframes (declared in index.css)
          so it costs nothing per frame after the initial setup. */}
      <div className="bsure-orb-mesh absolute inset-[-10%]" />

      {ORBS.map((orb) => (
        <span
          key={orb.id}
          data-orb={orb.id}
          className="absolute will-change-transform"
          style={{
            left: `${orb.x}%`,
            top: `${orb.y}%`,
            width: `${orb.sizeVmax}vmax`,
            height: `${orb.sizeVmax}vmax`,
            transform: "translate3d(calc(-50% + var(--orb-scroll-y, 0px)), -50%, 0)",
            background: `radial-gradient(closest-side, ${orb.color} 0%, transparent 70%)`,
            filter: "blur(40px)",
            mixBlendMode: tone === "light" ? "multiply" : "screen",
          }}
        />
      ))}
    </div>
  );
}

export default BgOrbs;
